
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';
/**
 * @Description: 商品详情操作日志模板（原值/新值对比项）
 * @date 2026/6/22
 */
import {setConfigCharacters} from '@/utils/common';

/**
 * ============================================================
 * 商品详情操作日志模板（字段编码对照表）
 * ============================================================
 * 说明：
 * 1. 以下为后端 operateContent 中 fieldKey 与前端展示文案的对照模板
 * 2. 通用格式：「字段名: 由原值 ${val.oldValue} 改为新值 ${val.newValue}」
 * 3. 当前实现已改为左右分栏对比（getProductDetailsLogCompareItem），
 *    本注释保留原始文案模板，便于后期维护对照
 * 4. 动态 label 入参：ymsSku / attrName / warehouseName / countryName / sizeValue / partValue
 * 5. 特殊模式：100023 仅展示原值（remove），100024 仅展示新值（add）
 * ============================================================
 *
 * const productDetailsLog = {
 *
 *   // ========== 基础信息 ==========
 *   '100000': (val) => `商品是否支持定制: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100001': (val) => `商品类目: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100002': (val) => `商品货号/SPU: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100003': (val) => `商品发货仓: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100004': (val) => `商品原产地: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100005': (val) => `商品轮播图: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100006': (val) => `图片类型: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100007': (val) => `商品中文标题: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100008': (val) => `商品英文标题: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 商品属性 ==========
 *   '100009': (val) => `商品基础属性【${val.attrName}】: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100010': (val) => `商品规格属性【${val.attrName}】: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 规格明细 ==========
 *   '100011': (val) => `${val.ymsSku}: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100012': (val) => `${val.ymsSku}-包装尺寸（长*宽*高 cm）: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100013': (val) => `${val.ymsSku}-重量(g): 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100014': (val) => `${val.ymsSku}-${val.countryName}供货价: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100015': (val) => `${val.ymsSku}-${val.warehouseName}仓库直发价: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100016': (val) => `${val.ymsSku}-${val.warehouseName}-库存: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 尺码表 ==========
 *   '100017': (val) => `${val.sizeValue}-尺码-${val.partValue}: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 服务与承诺 ==========
 *   '100018': (val) => `商品服务: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100019': (val) => `发货时效: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 商品销售建议 ==========
 *   '100020': (val) => `商品销售建议: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 阶梯价 ==========
 *   '100021': (val) => `${setConfigCharacters}阶梯供货价格-固定金额: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *   '100022': (val) => `${setConfigCharacters}阶梯供货价格-折扣: 由原值 ${val.oldValue} 改为新值 ${val.newValue}`,
 *
 *   // ========== 规格变更（当前实现扩展，图片无） ==========
 *   '100023': (val) => `商品移除规格: ${val.oldValue}`,           // mode: remove，仅原值
 *   '100024': (val) => `商品新增规格: ${val.newValue}`,           // mode: add，仅新值
 *
 * };
 *
 * ============================================================
 * 当前代码 label 与上述模板的差异（仅供对照）：
 * - 100021 当前 label：${setConfigCharacters}阶梯价格类型
 * - 100022 当前 label：${setConfigCharacters}分层价格
 * - 100015/100016 当前 warehouseName 为空时 label 省略仓库名
 * ============================================================
 */

/** 固定字段编码 → 展示名称 */
const FIXED_FIELD_LABELS = {
  '100000': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001603'),
  '100001': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001604'),
  '100002': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001605'),
  '100003': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001606'),
  '100004': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001607'),
  '100005': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001608'),
  '100006': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001609'),
  '100007': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001610'),
  '100008': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001611'),
  '100018': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001612'),
  '100019': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001613'),
  '100020': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000946'),
  '100023': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001614'),
  '100024': aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001615')
};

/**
 * 将日志值格式化为展示字符串
 * @param {*} val
 * @returns {string}
 */
const formatLogValue = (val) => {
  if (val === null || val === undefined) return '';
  if (Array.isArray(val)) return val.join('、');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

/**
 * 按字段编码与入参构建对比项展示名称
 * @param {string} fieldKey
 * @param {Object} val
 * @returns {string}
 */
const buildCompareLabel = (fieldKey, val) => {
  const ymsSku = val.ymsSku || '';
  const attrName = val.attrName || '';
  const warehouseName = val.warehouseName || '';
  const countryName = val.countryName || '';
  const sizeValue = val.sizeValue || '';
  const partValue = val.partValue || '';

  switch (fieldKey) {
    case '100009':
      return attrName ? `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001594')}${attrName}】` : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001616');
    case '100010':
      return attrName ? `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001595')}${attrName}】` : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001617');
    case '100011':
      return ymsSku || aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000976');
    case '100012':
      return ymsSku ? `${ymsSku}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001596')} cm）` : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000910');
    case '100013':
      return ymsSku ? `${ymsSku}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001597')}(g)` : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000589');
    case '100014':
      return ymsSku && countryName ? `${ymsSku}-${countryName}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000882')}` : (ymsSku || countryName ? `${ymsSku}-${countryName}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000882')}` : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000882'));
    case '100015':
      return ymsSku
        ? `${ymsSku}-${warehouseName ? `-${warehouseName}` : ''}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000873')}`
        : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000873');
    case '100016':
      return ymsSku
        ? `${ymsSku}-${warehouseName ? `-${warehouseName}` : ''}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001598')}`
        : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000890');
    case '100017':
      return sizeValue && partValue
        ? `${sizeValue}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001599')}${partValue}`
        : (sizeValue || partValue || aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000978'));
    case '100021':
      return `${setConfigCharacters()}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001600')}`;
    case '100022':
      return `${setConfigCharacters()}${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001601')}`;
    default: {
      if (FIXED_FIELD_LABELS[fieldKey]) {
        return FIXED_FIELD_LABELS[fieldKey];
      }
      const dynamicLabel = [ymsSku || attrName || sizeValue, countryName || warehouseName || partValue]
        .filter(Boolean)
        .join('-');
      return dynamicLabel || `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001602')}${fieldKey}`;
    }
  }
};

/**
 * 生成单条原值/新值对比数据（供左右分栏展示）
 * @param {string} fieldKey - 字段编码，如 '100007'
 * @param {Object} val - 模板入参，含 oldValue/newValue 及动态 label 字段
 * @returns {{label: string, oldValue: string, newValue: string, mode: string}|null}
 */
export const getProductDetailsLogCompareItem = (fieldKey, val) => {
  if (!val || typeof val !== 'object') return null;

  const label = buildCompareLabel(fieldKey, val);

  if (fieldKey === '100005' || val.valueType === 'imageList') {
    const oldImages = Array.isArray(val.oldImages) ? val.oldImages : [];
    const newImages = Array.isArray(val.newImages) ? val.newImages : [];
    if (!oldImages.length && !newImages.length) return null;
    return {
      label,
      valueType: 'imageList',
      oldImages,
      newImages,
      mode: 'compare'
    };
  }

  const oldValue = formatLogValue(val.oldValue);
  const newValue = formatLogValue(val.newValue);

  if (fieldKey === '100023') {
    if (!oldValue) return null;
    return {label, oldValue, newValue: '', mode: 'remove'};
  }
  if (fieldKey === '100024') {
    if (!newValue) return null;
    return {label, oldValue: '', newValue, mode: 'add'};
  }
  if (!oldValue && !newValue) return null;

  return {label, oldValue, newValue, mode: 'compare'};
};
